/**
 * ServicesView — "Services" tab on the CRM dashboard (May 2026).
 *
 * Mirrors the structure of TodayView ("Leads" tab) but powered by the new
 * /api/dashboard/services/* endpoints and the db.service_leads collection.
 *
 * Rows:
 *   1. 4 KPI cards: Service Revenue / Service Sales / Service Leads / Conversion
 *   2. 3 funnel pulse cards: Draft→Contacted / Quoted→Booked / Same-Day
 *   3. Expenditure breakdown: Google Ads (pending) / Meta / Manual
 *   4. Service Sales by Executive table
 *
 * Revenue + Expenditure are RBAC-gated server-side — for non-finance users
 * the API returns `revenue_redacted: true` and we show a "—" instead of {activeCurrencySymbol()}.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  IndianRupee, TrendingUp, Target, Users, RefreshCw, AlertCircle, Wallet,
  Zap, Send, X, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import CityAnalysis from "./CityAnalysis";
import ServicePaymentLinks from "./ServicePaymentLinks";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PERIODS = [
  { key: "today",      label: "Today"      },
  { key: "yesterday",  label: "Yesterday"  },
  { key: "wtd",        label: "WTD"        },
  { key: "mtd",        label: "MTD"        },
  { key: "7d",         label: "7D"         },
  { key: "30d",        label: "30D"        },
];

// A custom range travels as the SAME `period` param — "2026-06-01:2026-06-12" —
// so every Services endpoint honours it without a second plumbing path.
// LOCAL date (not UTC) because the backend anchors everything to IST.
const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayISO = () => toISO(new Date());
const isCustomPeriod = (p) => /^\d{4}-\d{2}-\d{2}/.test(p || "");
const rangeOf = (p) => {
  const [start, end] = (p || "").split(":");
  return { start, end: end || start };
};
const prettyRange = ({ start, end }) => {
  const f = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return start === end ? f(start) : `${f(start)} → ${f(end)}`;
};
// Mirrors the backend enums so the date inputs show the chip's actual window.
const presetRange = (key) => {
  const now = new Date();
  const end = todayISO();
  const back = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return toISO(d); };
  switch (key) {
    case "yesterday": return { start: back(1), end: back(1) };
    case "wtd":       return { start: back((now.getDay() + 6) % 7), end };
    case "mtd":       return { start: toISO(new Date(now.getFullYear(), now.getMonth(), 1)), end };
    case "7d":        return { start: back(6), end };
    case "30d":       return { start: back(29), end };
    default:          return { start: end, end };
  }
};
// A custom range should also stretch the "cost per paid" trend, which is a
// rolling window rather than a period (capped so the chart stays readable).
const trendDaysFor = (p) => {
  if (!isCustomPeriod(p)) return 30;
  const { start, end } = rangeOf(p);
  const days = Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000) + 1;
  return Math.min(120, Math.max(7, days));
};

const fmt  = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const fmtK = (n) => {
  if (n == null) return "—";
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10_000_000) return `${activeCurrencySymbol()}${(v / 10_000_000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100_000)    return `${activeCurrencySymbol()}${(v / 100_000).toFixed(1)}L`;
  if (Math.abs(v) >= 1_000)      return `${activeCurrencySymbol()}${(v / 1_000).toFixed(1)}K`;
  return `${activeCurrencySymbol()}${v.toFixed(0)}`;
};

// "as of HH:MM" — render the metrics snapshot time in IST.
const fmtAsOf = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata",
    });
  } catch { return null; }
};

// Per-channel accent so the source-wise revenue split matches the Service Leads badges.
const SOURCE_COLORS = {
  "Google Ads":    { dot: "bg-blue-500",    bar: "bg-blue-500"    },
  "Meta":          { dot: "bg-indigo-500",  bar: "bg-indigo-500"  },
  "WhatsApp":      { dot: "bg-emerald-500", bar: "bg-emerald-500" },
  "Website":       { dot: "bg-violet-500",  bar: "bg-violet-500"  },
  "Referral":      { dot: "bg-amber-500",   bar: "bg-amber-500"   },
  "Direct/Others": { dot: "bg-slate-400",   bar: "bg-slate-400"   },
};
const srcColor = (ch) => SOURCE_COLORS[ch] || SOURCE_COLORS["Direct/Others"];

// Hex equivalents for recharts (Tailwind classes don't work as SVG strokes).
const SOURCE_HEX = {
  "Google Ads": "#3b82f6", "Meta": "#6366f1", "WhatsApp": "#10b981",
  "Website": "#8b5cf6", "Referral": "#f59e0b", "Direct/Others": "#94a3b8",
};
const srcHex = (ch) => SOURCE_HEX[ch] || SOURCE_HEX["Direct/Others"];

// ── Card primitives (mirror TodayView visuals) ──
function PrimaryKpi({ icon: Icon, label, value, sub, accent = "blue", testid }) {
  const accents = {
    blue: "text-blue-600",
    emerald: "text-emerald-600",
    violet: "text-violet-600",
    amber: "text-amber-600",
  };
  return (
    <div
      className="bg-white rounded-xl border border-slate-200/80 p-5 hover:shadow-md transition-shadow"
      data-testid={testid || `svc-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <Icon className={`w-4 h-4 ${accents[accent]}`} strokeWidth={2} />
      </div>
      <div className="text-3xl font-bold text-slate-900 tracking-tight leading-none">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1.5">{sub}</div>}
    </div>
  );
}

function FunnelCard({ label, count, pool, pct, accent = "amber" }) {
  const accents = {
    amber:   { ring: "border-amber-200",   bg: "bg-amber-50",   badge: "bg-amber-100 text-amber-700",   text: "text-amber-700"   },
    violet:  { ring: "border-violet-200",  bg: "bg-violet-50",  badge: "bg-violet-100 text-violet-700", text: "text-violet-700"  },
    emerald: { ring: "border-emerald-200", bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", text: "text-emerald-700" },
  };
  const a = accents[accent] || accents.amber;
  return (
    <div className={`rounded-xl border ${a.ring} ${a.bg} p-4`} data-testid={`svc-funnel-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-xs font-semibold text-slate-600 mb-2">{label}</div>
      <div className="flex items-end gap-2">
        <span className={`text-2xl font-bold ${a.text}`}>{count}</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${a.badge}`}>{pct}%</span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">from {fmt(pool)} in pipeline</div>
    </div>
  );
}

function ChannelRow({ name, amount, status, currency = "INR" }) {
  const isPending = status === "pending_approval";
  const isError   = status === "error";
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
      <div className="flex items-center gap-3">
        <span className={`w-2 h-2 rounded-full ${isPending ? "bg-amber-400" : isError ? "bg-rose-500" : "bg-emerald-500"}`} />
        <span className="text-sm font-medium text-slate-700">{name}</span>
        {isPending && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
            PENDING
          </span>
        )}
      </div>
      <div className="text-sm font-semibold text-slate-900">
        {isPending ? "—" : fmtK(amount)}
      </div>
    </div>
  );
}

function GadsStat({ label, value, accent = "slate" }) {
  const c = accent === "amber" ? "text-amber-700"
    : accent === "emerald" ? "text-emerald-700"
    : "text-slate-900";
  return (
    <div className="rounded-lg border border-slate-200/80 bg-slate-50/60 px-3 py-2">
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${c} leading-tight`}>{value}</div>
    </div>
  );
}

// ── "Received" card: New advance vs Old collections + source-wise split ──
function ReceivedBreakdown({ revenue, asOf, onSourceClick }) {
  const received    = revenue?.received_amount ?? 0;
  const newAdvance  = revenue?.received_new ?? 0;
  const collections = revenue?.received_collections ?? 0;
  const bySource    = revenue?.by_source || [];
  const maxSrc      = bySource.reduce((mx, s) => Math.max(mx, s.amount || 0), 0) || 1;
  const asOfStr     = fmtAsOf(asOf);

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-received-breakdown">
      <div className="flex items-center gap-2 mb-4">
        <IndianRupee className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-700">Received</h3>
        {asOfStr && (
          <span className="text-[11px] text-slate-400 ml-auto" data-testid="svc-received-asof">
            as of {asOfStr} IST
          </span>
        )}
      </div>

      {/* New advance vs Old collections */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2.5" data-testid="svc-received-total">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Total received</div>
          <div className="text-xl font-bold text-slate-900 leading-tight">{fmtK(received)}</div>
        </div>
        <div className="rounded-lg border border-slate-200/80 bg-slate-50/60 px-3 py-2.5" data-testid="svc-received-new">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">New advance</div>
          <div className="text-xl font-bold text-emerald-700 leading-tight">{fmtK(newAdvance)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">this period’s bookings</div>
        </div>
        <div className="rounded-lg border border-slate-200/80 bg-slate-50/60 px-3 py-2.5" data-testid="svc-received-collections">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Old collections</div>
          <div className="text-xl font-bold text-slate-700 leading-tight">{fmtK(collections)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">balance / spares on earlier bookings</div>
        </div>
      </div>

      {/* Source-wise revenue split */}
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">By source</div>
        <span className="text-[10px] text-slate-400 normal-case font-normal">· tap for customers</span>
        <div className="flex items-center gap-2 ml-auto text-[10px] text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500" />new advance</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" />old collections</span>
        </div>
      </div>
      {bySource.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">No money received in this period.</p>
      ) : (
        <div className="space-y-2.5" data-testid="svc-received-by-source">
          {bySource.map((s) => {
            const c = srcColor(s.channel);
            const newAmt = s.received_new ?? s.amount;
            const oldAmt = s.received_collections ?? 0;
            const newPct = Math.round((newAmt / maxSrc) * 100);
            const oldPct = Math.round((oldAmt / maxSrc) * 100);
            const slug = s.channel.toLowerCase().replace(/[^a-z]+/g, "-");
            return (
              <div
                key={s.channel}
                role="button"
                tabIndex={0}
                onClick={() => onSourceClick && onSourceClick(s.channel)}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onSourceClick) { e.preventDefault(); onSourceClick(s.channel); } }}
                className="group -mx-1.5 px-1.5 py-1 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid={`svc-received-src-${slug}`}
                title={`View ${s.channel} paid customers this period`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${c.dot} shrink-0`} />
                  <span className="text-sm text-slate-700 w-28 shrink-0 group-hover:text-slate-900">{s.channel}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden flex">
                    <div className={`h-full ${c.bar}`} style={{ width: `${Math.max(newAmt > 0 ? 3 : 0, newPct)}%` }} />
                    <div className="h-full bg-slate-300" style={{ width: `${Math.max(oldAmt > 0 ? 3 : 0, oldPct)}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 w-16 text-right shrink-0">{fmtK(s.amount)}</span>
                </div>
                {oldAmt > 0 && (
                  <div className="text-[10px] text-slate-400 ml-5 mt-0.5" data-testid={`svc-received-src-split-${slug}`}>
                    new {fmtK(newAmt)} · <span className="text-slate-500">old {fmtK(oldAmt)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Received drilldown modal: leads whose CASH landed this period for a channel ──
function ReceivedDrilldown({ period, channel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API_URL}/dashboard/services/received-leads`, { params: { period, channel } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData({ leads: [], error: true }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period, channel]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const leads = data?.leads || [];
  const c = srcColor(channel);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
         onClick={onClose} data-testid="svc-received-drilldown">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[82vh] flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
          <div>
            <h3 className="text-sm font-semibold text-slate-800" data-testid="svc-received-drilldown-title">
              {channel} — paid this period
            </h3>
            <p className="text-[11px] text-slate-400">
              {loading ? "Loading…"
                : `${data?.count ?? 0} customer${(data?.count ?? 0) === 1 ? "" : "s"} · ${fmtK(data?.total_received ?? 0)} received`
                  + (data?.received_collections ? ` (new ${fmtK(data.received_new)} · old ${fmtK(data.received_collections)})` : "")}
            </p>
          </div>
          <button onClick={onClose}
                  className="ml-auto p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                  data-testid="svc-received-drilldown-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-400 text-sm">Loading customers…</div>
          ) : leads.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm" data-testid="svc-received-drilldown-empty">
              No {channel} payments landed in this period.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="svc-received-drilldown-table">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                  <th className="text-left py-2 px-4">Customer</th>
                  <th className="text-left py-2 px-2">City</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-4">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50"
                      data-testid={`svc-received-drilldown-row-${l.id}`}>
                    <td className="py-2.5 px-4">
                      <div className="font-medium text-slate-800">{l.customer_name}</div>
                      <div className="text-[11px] text-slate-400">{l.mobile}</div>
                    </td>
                    <td className="py-2.5 px-2 text-slate-600">{l.city || "—"}</td>
                    <td className="py-2.5 px-2 text-slate-500 text-xs">{l.status}</td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="font-semibold text-slate-900">{fmtK(l.received)}</span>
                      <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        l.kind === "new" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {l.kind === "new" ? "new" : "old"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── "Source Performance" matrix: ad spend → leads → funnel → PAID → revenue,
//    per marketing channel, with the honest cost-per-conversion (spend ÷ paid),
//    an optional per-city split, and a cost-per-paid trend chart.
function SourcePerformance({ sperf, gads, strend, srcCity, onCityChange, srcBasis, onBasisChange, srcLoading }) {
  if (sperf?.forbidden) return null;
  const rows   = sperf?.rows || [];
  const totals = sperf?.totals;
  if (!totals) return null;
  const received = (srcBasis || "cohort") === "received";

  const money   = (v) => (v == null ? "—" : fmtK(v));
  const costPer = (r) =>
    r.cost_per_paid != null ? fmtK(r.cost_per_paid)
    : (r.ad_spend > 0 ? "no paid" : "—");
  const roasStr = (r) => (r.roas == null ? "—" : `${r.roas}×`);

  const gRow = rows.find((r) => r.channel === "Google Ads");
  const gReported = gads && !gads.forbidden ? gads.totals?.conversions : null;
  const cityOpts = (sperf?.city_options || []).slice(0, 5);

  // City ROAS compare — per-metro cost-per-paid (Google spend ÷ paid). Only
  // cities with Google spend are comparable; flag the cheapest to steer budget.
  const cityComp = (sperf?.city_comparison || []).filter((c) => c.ad_spend > 0);
  const bestCity = (() => {
    const withCpp = cityComp.filter((c) => c.cost_per_paid != null);
    if (!withCpp.length) return null;
    return withCpp.reduce((a, b) => (b.cost_per_paid < a.cost_per_paid ? b : a)).city;
  })();
  // ROAS alert — metros that are LOSING money (revenue < spend → ROAS < 1×).
  const losingCities = cityComp.filter((c) => c.roas != null && c.roas < 1);

  // Avg revenue per service, per unique paying customer — ALL cities with
  // paying customers (not just ad-spend metros). Sorted by cash collected desc.
  const cityArpu = (sperf?.city_comparison || [])
    .filter((c) => (c.paying_customers || 0) > 0)
    .sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

  const CityBtn = ({ value, label, count }) => {
    const active = (srcCity || "") === value;
    return (
      <button
        onClick={() => onCityChange(value)}
        className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
          active ? "bg-blue-600 text-white border-blue-600"
                 : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
        data-testid={`svc-source-city-${(value || "all").toLowerCase()}`}
      >
        {label}{count != null && <span className={`ml-1 ${active ? "text-blue-100" : "text-slate-400"}`}>{count}</span>}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-source-performance">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-4 h-4 text-blue-600" />
        <h3 className="text-sm font-semibold text-slate-700">Source Performance — real conversions</h3>
        {srcLoading && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
        {sperf?.as_of && (
          <span className="text-[11px] text-slate-400 ml-auto">as of {fmtAsOf(sperf.as_of)} IST</span>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Attributed by the click (gclid/fbclid/utm), not the landing page. <strong>Conv = PAID</strong> (not
        Google’s in-tag lead count). Cost/Paid = ad spend ÷ leads that actually paid.
      </p>

      {/* Revenue basis toggle — cohort (acquisition) vs cash received this period */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="svc-source-basis-toggle">
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mr-1">Revenue basis</span>
        {[
          { key: "cohort",   label: "Cohort revenue" },
          { key: "received", label: "Cash received" },
        ].map((b) => {
          const active = (srcBasis || "cohort") === b.key;
          return (
            <button
              key={b.key}
              onClick={() => onBasisChange && onBasisChange(b.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                active ? "bg-blue-600 text-white border-blue-600"
                       : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`svc-source-basis-${b.key}`}
            >
              {b.label}
            </button>
          );
        })}
        <span className="text-[11px] text-slate-400">
          {received
            ? "Cash that landed this period, by source (reconciles with the Received panel)."
            : "Money collected on leads created this period (acquisition cohort)."}
        </span>
      </div>

      {/* City toggle — which metro each ad rupee really converts */}
      {cityOpts.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="svc-source-city-toggle">
          <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mr-1">City</span>
          <CityBtn value="" label="All" />
          {cityOpts.map((c) => <CityBtn key={c.city} value={c.city} label={c.city} count={c.leads} />)}
        </div>
      )}

      {/* ROAS alert — flag loss-making metros (ROAS < 1× = spending more than they earn) */}
      {losingCities.length > 0 && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[12px] text-rose-800"
             data-testid="svc-source-roas-alert">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />
          <div>
            <span className="font-semibold">
              Losing money on {losingCities.map((c) => c.city).join(", ")}
            </span>{" — "}
            {losingCities.map((c, i) => (
              <span key={c.city}>
                {i > 0 && "; "}{c.city} earns {fmtK(c.revenue)} on {fmtK(c.ad_spend)} spend (ROAS {c.roas}×)
              </span>
            ))}. Consider pausing or fixing {losingCities.length > 1 ? "these campaigns" : "this campaign"}.
          </div>
        </div>
      )}

      {/* City ROAS compare — cost-per-paid per metro, cheapest highlighted */}
      {cityComp.length >= 2 && (
        <div className="mb-4" data-testid="svc-source-city-compare">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
            City cost-per-paid &amp; avg value{" "}
            <span className="text-slate-400 normal-case font-normal">
              (ad cost ÷ paid on top · billed &amp; collected per customer below)
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {cityComp.map((c) => {
              const isBest = c.city === bestCity;
              const isLosing = c.roas != null && c.roas < 1;
              return (
                <div
                  key={c.city}
                  className={`px-3 py-2 rounded-lg border ${
                    isLosing ? "bg-rose-50 border-rose-200"
                      : isBest ? "bg-emerald-50 border-emerald-200"
                      : "bg-slate-50 border-slate-200"}`}
                  data-testid={`svc-source-city-compare-${c.city.toLowerCase()}`}
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-slate-700">{c.city}</span>
                    {isBest && (
                      <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                        cheapest
                      </span>
                    )}
                    {isLosing && (
                      <span className="text-[10px] font-medium text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                        losing
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5">
                    <span className="text-lg font-bold text-slate-800">
                      {c.cost_per_paid != null ? fmtK(c.cost_per_paid) : "—"}
                    </span>
                    <span className="text-[11px] text-slate-400"> ad cost / paid</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {fmt(c.paid)} paid · {fmtK(c.ad_spend)} spend · ROAS{" "}
                    <span className={isLosing ? "text-rose-600 font-semibold" : ""}>
                      {c.roas != null ? `${c.roas}×` : "—"}
                    </span>
                  </div>
                  {c.avg_billed_per_customer != null && (
                    <div className="mt-1 pt-1 border-t border-slate-200/70 text-[11px]"
                         data-testid={`svc-source-city-arpu-${c.city.toLowerCase()}`}>
                      <span className="font-semibold text-slate-700">{fmtK(c.avg_billed_per_customer)}</span>
                      <span className="text-slate-400"> billed · </span>
                      <span className="font-semibold text-slate-600">{fmtK(c.avg_collected_per_customer)}</span>
                      <span className="text-slate-400"> collected /customer</span>
                      <span className="text-slate-400"> · {fmt(c.paying_customers)} cust.</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Avg revenue per service by city — every city with paying customers */}
      {cityArpu.length > 0 && (
        <div className="mb-4" data-testid="svc-source-city-arpu-table">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
            Avg revenue per service by city{" "}
            <span className="text-slate-400 normal-case font-normal">
              (per unique paying customer · incl. extras/spares)
            </span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-100">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] font-medium text-slate-500 uppercase tracking-wide bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left  py-1.5 px-2">City</th>
                  <th className="text-right py-1.5 px-2">Customers</th>
                  <th className="text-right py-1.5 px-2">Billed / cust</th>
                  <th className="text-right py-1.5 px-2">Collected / cust</th>
                  <th className="text-right py-1.5 px-2">Total collected</th>
                </tr>
              </thead>
              <tbody>
                {cityArpu.map((c) => (
                  <tr key={c.city} className="border-b border-slate-50 last:border-0"
                      data-testid={`svc-source-city-arpu-row-${c.city.toLowerCase()}`}>
                    <td className="text-left  py-1.5 px-2 font-medium text-slate-700">{c.city}</td>
                    <td className="text-right py-1.5 px-2 text-slate-600">{fmt(c.paying_customers)}</td>
                    <td className="text-right py-1.5 px-2 font-semibold text-slate-700">
                      {c.avg_billed_per_customer != null ? fmtK(c.avg_billed_per_customer) : "—"}
                    </td>
                    <td className="text-right py-1.5 px-2 text-slate-600">
                      {c.avg_collected_per_customer != null ? fmtK(c.avg_collected_per_customer) : "—"}
                    </td>
                    <td className="text-right py-1.5 px-2 text-slate-500">
                      {c.revenue != null ? fmtK(c.revenue) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Google reconciliation banner — the crux of the gap */}
      {gRow && gReported != null && !srcCity && (
        <div className="mb-4 px-3 py-2 rounded-md bg-blue-50/70 border border-blue-100 text-[12px] text-slate-700"
             data-testid="svc-source-google-reconcile">
          Google reports <strong>{fmt(gReported)}</strong> conversions · CRM captured{" "}
          <strong>{fmt(gRow.leads)}</strong> Google-attributed leads →{" "}
          <strong>{fmt(gRow.paid)}</strong> paid. Real cost per paid:{" "}
          <strong className="text-amber-700">{gRow.cost_per_paid != null ? fmtK(gRow.cost_per_paid) : "— (0 paid)"}</strong>
          {gads?.totals?.cpl != null && (
            <> vs Google’s stated <span className="line-through text-slate-400">{fmtK(gads.totals.cpl)}</span>/conv.</>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="svc-source-performance-table">
          <thead>
            <tr className="text-xs font-medium text-slate-500 uppercase tracking-wide border-b border-slate-100">
              <th className="text-left  py-2 px-2">Source</th>
              <th className="text-right py-2 px-2">Leads</th>
              <th className="text-right py-2 px-2">Contacted</th>
              <th className="text-right py-2 px-2">Quoted</th>
              <th className="text-right py-2 px-2">Booked</th>
              <th className="text-right py-2 px-2">Paid</th>
              <th className="text-right py-2 px-2">Conv %</th>
              <th className="text-right py-2 px-2" data-testid="svc-source-revenue-header">{received ? "Cash Rcvd" : "Revenue"}</th>
              <th className="text-right py-2 px-2">Ad Spend</th>
              <th className="text-right py-2 px-2">CPL</th>
              <th className="text-right py-2 px-2">Cost/Paid</th>
              <th className="text-right py-2 px-2">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={12} className="py-6 text-center text-slate-400">No leads in this period{srcCity ? ` for ${srcCity}` : ""}.</td></tr>
            ) : rows.map((r) => {
              const c = srcColor(r.channel);
              const wasted = r.ad_spend > 0 && r.paid === 0;
              return (
                <tr key={r.channel} className="border-b border-slate-50 hover:bg-slate-50"
                    data-testid={`svc-source-row-${r.channel.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <td className="py-2 px-2">
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                      <span className="font-medium text-slate-800">{r.channel}</span>
                    </span>
                  </td>
                  <td className="text-right py-2 px-2 text-slate-700">{fmt(r.leads)}</td>
                  <td className="text-right py-2 px-2 text-slate-500">{fmt(r.contacted)}</td>
                  <td className="text-right py-2 px-2 text-slate-500">{fmt(r.quoted)}</td>
                  <td className="text-right py-2 px-2 text-slate-600">{fmt(r.booked)}</td>
                  <td className="text-right py-2 px-2 font-semibold text-emerald-700">{fmt(r.paid)}</td>
                  <td className="text-right py-2 px-2 text-slate-600">{r.paid_pct}%</td>
                  <td className="text-right py-2 px-2 font-semibold text-slate-900">{money(r.revenue)}</td>
                  <td className="text-right py-2 px-2 text-slate-700">{r.ad_spend ? fmtK(r.ad_spend) : "—"}</td>
                  <td className="text-right py-2 px-2 text-slate-600">{money(r.cpl)}</td>
                  <td className={`text-right py-2 px-2 font-medium ${wasted ? "text-rose-600" : "text-amber-700"}`}>{costPer(r)}</td>
                  <td className={`text-right py-2 px-2 font-medium ${r.roas != null && r.roas >= 1 ? "text-emerald-700" : "text-slate-600"}`}>{roasStr(r)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 font-semibold text-slate-900" data-testid="svc-source-total-row">
              <td className="py-2 px-2">All sources</td>
              <td className="text-right py-2 px-2">{fmt(totals.leads)}</td>
              <td className="text-right py-2 px-2 text-slate-500">{fmt(totals.contacted)}</td>
              <td className="text-right py-2 px-2 text-slate-500">{fmt(totals.quoted)}</td>
              <td className="text-right py-2 px-2">{fmt(totals.booked)}</td>
              <td className="text-right py-2 px-2 text-emerald-700">{fmt(totals.paid)}</td>
              <td className="text-right py-2 px-2">{totals.paid_pct}%</td>
              <td className="text-right py-2 px-2">{money(totals.revenue)}</td>
              <td className="text-right py-2 px-2">{totals.ad_spend ? fmtK(totals.ad_spend) : "—"}</td>
              <td className="text-right py-2 px-2">{money(totals.cpl)}</td>
              <td className="text-right py-2 px-2 text-amber-700">{money(totals.cost_per_paid)}</td>
              <td className="text-right py-2 px-2">{roasStr(totals)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">
        {srcCity
          ? <>Showing <strong>{srcCity}</strong> only — ad spend is the Google campaign(s) for this metro (Meta/manual have no city tag).</>
          : <>Blended CPL = total ad spend ÷ all CRM leads. Blended Cost/Paid = ad spend ÷ leads that paid (true cost per acquisition). Organic channels carry ₹0 spend.</>}
      </p>

      {/* Cost-per-paid trend (7-day rolling) */}
      <SourceTrend strend={strend} />
    </div>
  );
}

// ── Cost-per-paid trend line chart (7-day rolling), one line per paid channel ──
function SourceTrend({ strend }) {
  const dates = strend?.dates || [];
  const chans = (strend?.channels || []).filter((c) => c.total_spend > 0);
  if (!dates.length || !chans.length) return null;

  const data = dates.map((d, i) => {
    const row = { date: d.slice(5).replace("-", "/") };  // MM/DD
    chans.forEach((c) => { row[c.channel] = c.points[i]?.cost_per_paid_7d ?? null; });
    return row;
  });

  return (
    <div className="mt-5 pt-4 border-t border-slate-100" data-testid="svc-source-trend">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-3.5 h-3.5 text-slate-500" />
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Cost per paid — last {strend?.days || 30} days (7-day rolling)
        </h4>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Lower &amp; falling = ads getting more efficient at winning real paying customers. Rising = quality slipping — brief the ads team.
      </p>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={52}
                   tickFormatter={(v) => fmtK(v)} />
            <RTooltip
              formatter={(v, name) => [v == null ? "—" : fmtK(v), name]}
              labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {chans.map((c) => (
              <Line key={c.channel} type="monotone" dataKey={c.channel}
                    stroke={srcHex(c.channel)} strokeWidth={2} dot={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Main component ──
export default function ServicesView() {
  const [period,  setPeriod]  = useState("today");  // Services dashboard opens on Today (CEO request 2026-08); chips switch to MTD for the ROAS view
  // Custom range lives inside `period` as "start:end"; `range` keeps the two
  // inputs in step with whichever window is actually on screen.
  const [range,   setRange]   = useState({ start: todayISO(), end: todayISO() });
  const custom = isCustomPeriod(period);
  const setCustomRange = (start, end) => {
    setRange({ start, end });
    setPeriod(`${start}:${end}`);
  };
  const pickPreset = (key) => {
    setRange(presetRange(key));   // so the pickers never contradict the active chip
    setPeriod(key);
  };
  const [metrics, setMetrics] = useState(null);
  const [funnel,  setFunnel]  = useState(null);
  const [exp,     setExp]     = useState(null);
  const [reps,    setReps]    = useState(null);
  const [gads,    setGads]    = useState(null);
  const [sperf,   setSperf]   = useState(null);   // source-performance matrix
  const [strend,  setStrend]  = useState(null);   // source cost-per-paid trend
  const [srcCity, setSrcCity] = useState("");     // "" = all cities
  const [srcBasis, setSrcBasis] = useState("received"); // "cohort" | "received"
  const [drillChannel, setDrillChannel] = useState(null); // Received panel drilldown
  const [srcLoading, setSrcLoading] = useState(false);
  const [ocv,     setOcv]     = useState(null);   // offline-conversions status
  const [wac,     setWac]     = useState(null);   // WhatsApp chat-started conversions (founder-only)
  const [sweeping, setSweeping] = useState(false);
  const [wacSweeping, setWacSweeping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);
  const allSeqRef = useRef(0);   // stale-response guards: the newest range wins
  const srcSeqRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const seq = ++allSeqRef.current;   // start+end edits overlap; last pick wins
    setLoading(true);
    setErr(null);
    try {
      const params = { period };
      // Run in parallel — expenditure can 403 for non-finance users; swallow that
      const [mRes, fRes, eRes, rRes, gRes, oRes, wRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/services/metrics`,      { params }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/funnel`,       { params }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/expenditure`,  { params }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/by-executive`, { params }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/google-ads/live-performance`, { params }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/offline-conversions/status`).catch(e => ({ error: e })),
        // Founder-only — 403 for everyone else, and the card simply isn't rendered.
        axios.get(`${API_URL}/dashboard/services/whatsapp-chat-conversions/status`).catch(e => ({ error: e })),
      ]);
      if (seq !== allSeqRef.current) return;   // a newer range already answered
      if (mRes.error) throw mRes.error;
      setMetrics(mRes.data);
      setFunnel(fRes.error ? null : fRes.data);
      // exp 403 = user not finance — show a friendly placeholder instead
      setExp(eRes.error ? { forbidden: eRes.error?.response?.status === 403 } : eRes.data);
      setReps(rRes.error ? null : rRes.data);
      setGads(gRes.error ? { forbidden: gRes.error?.response?.status === 403 } : gRes.data);
      setOcv(oRes.error ? { forbidden: oRes.error?.response?.status === 403 } : oRes.data);
      setWac(wRes.error ? { forbidden: true } : wRes.data);
    } catch (e) {
      if (seq !== allSeqRef.current) return;
      setErr(e.response?.data?.detail || e.message || "Failed to load");
      toast.error("Failed to load service dashboard");
    } finally {
      if (seq === allSeqRef.current) setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Source Performance + trend fetch independently so the city toggle can
  // refresh just that card (period change refreshes it too).
  const fetchSource = useCallback(async () => {
    const seq = ++srcSeqRef.current;
    setSrcLoading(true);
    try {
      const spParams = { period, basis: srcBasis, ...(srcCity ? { city: srcCity } : {}) };
      const stParams = { days: trendDaysFor(period), ...(srcCity ? { city: srcCity } : {}) };
      const [spRes, stRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/services/source-performance`, { params: spParams }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/dashboard/services/source-trend`,       { params: stParams }).catch(e => ({ error: e })),
      ]);
      if (seq !== srcSeqRef.current) return;
      setSperf(spRes.error ? { forbidden: spRes.error?.response?.status === 403 } : spRes.data);
      setStrend(stRes.error ? null : stRes.data);
    } finally {
      if (seq === srcSeqRef.current) setSrcLoading(false);
    }
  }, [period, srcCity, srcBasis]);

  useEffect(() => { fetchSource(); }, [fetchSource]);

  const runSweep = useCallback(async () => {
    setSweeping(true);
    try {
      const res = await axios.post(`${API_URL}/dashboard/services/offline-conversions/run-sweep`);
      const d = res.data || {};
      if (d.already_running) toast.info(d.message || "A sweep is already running — watch the tally.");
      else toast.success(d.message || "Sweep started in the background — the tally will update shortly.");
      // Poll the status a few times so the card reflects the climbing tally.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API_URL}/dashboard/services/offline-conversions/status`).catch(() => null);
        if (st?.data) setOcv(st.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  }, []);

  const runWacSweep = useCallback(async () => {
    setWacSweeping(true);
    try {
      const res = await axios.post(`${API_URL}/dashboard/services/whatsapp-chat-conversions/run-sweep`);
      const d = res.data || {};
      toast[d.already_running ? "info" : "success"](d.message || "Sweep started.");
      for (let i = 0; i < 4; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API_URL}/dashboard/services/whatsapp-chat-conversions/status`).catch(() => null);
        if (st?.data) setWac(st.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally {
      setWacSweeping(false);
    }
  }, []);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400" data-testid="svc-loading">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading service metrics…
      </div>
    );
  }
  if (err && !metrics) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-rose-600" data-testid="svc-error">
        <AlertCircle className="w-6 h-6 mb-2" />
        <div className="text-sm">{err}</div>
      </div>
    );
  }

  const m = metrics || {};
  const f = funnel  || {};
  const e = exp     || {};
  const r = reps    || {};
  const g = gads    || {};
  const o = ocv     || {};
  const w = wac     || {};

  const revRedacted = m?.revenue?.redacted;
  // Option (b): make "Service Revenue" honest — split the headline into money
  // from THIS period's bookings vs collections (balance/spares) on earlier
  // bookings (the 2026-06 spares P0 money). Falls back to the bookings count
  // when there are no earlier-booking collections, or on older API payloads.
  const revFromBookings    = m?.revenue?.from_bookings;
  const revFromCollections = m?.revenue?.from_collections;
  const revSub = revRedacted
    ? "Hidden for your role"
    : (revFromCollections > 0
        ? (
            <span data-testid="svc-revenue-breakdown">
              <span className="text-emerald-600 font-medium">{fmtK(revFromBookings)}</span>
              {" from bookings · "}
              <span className="text-slate-500">{fmtK(revFromCollections)} collections</span>
            </span>
          )
        : `${fmt(m?.leads?.booked)} bookings`);
  const adSpendSub  = e?.total != null && !e.forbidden
    ? `Ad spend: ${fmtK(e.total)}`
    : (e?.forbidden ? "Ad spend: hidden" : "Ad spend: —");

  return (
    <div className="space-y-6" data-testid="services-view">
      {/* Period chips + custom date range (the CEO asked for the Leads tab's
          picker here too, 18 Oct 2026 — every card below honours it) */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-wrap items-center gap-1 bg-white rounded-lg border border-slate-200 p-1" data-testid="svc-period-chips">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => pickPreset(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.key
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`svc-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="w-px h-6 bg-slate-200 hidden sm:block" />
          <div className={`flex items-center gap-2 rounded-lg p-1 border ${custom ? "border-blue-300 bg-blue-50/60" : "border-transparent"}`}>
            <div className="relative">
              <input
                type="date"
                value={range.start}
                max={todayISO()}
                onChange={(e) => e.target.value && setCustomRange(e.target.value, range.end >= e.target.value ? range.end : e.target.value)}
                className="pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-[132px]"
                data-testid="svc-date-start"
              />
              <Calendar className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <span className="text-xs text-slate-400">to</span>
            <div className="relative">
              <input
                type="date"
                value={range.end}
                min={range.start}
                max={todayISO()}
                onChange={(e) => e.target.value && setCustomRange(range.start <= e.target.value ? range.start : e.target.value, e.target.value)}
                className="pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-[132px]"
                data-testid="svc-date-end"
              />
              <Calendar className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md border border-slate-200 disabled:opacity-50"
          data-testid="svc-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="-mt-3 flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
        {custom && (
          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium border border-blue-200" data-testid="svc-active-range">
            Showing {prettyRange(range)}
          </span>
        )}
        {m?.as_of && (
          <span data-testid="svc-dashboard-asof">Live as of {fmtAsOf(m.as_of)} IST</span>
        )}
      </div>

      {/* Row 1 — 4 KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="svc-kpis">
        <PrimaryKpi
          icon={IndianRupee}
          label="Service Revenue"
          value={revRedacted ? "—" : fmtK(m?.revenue?.amount)}
          sub={revSub}
          accent="amber"
        />
        <PrimaryKpi
          icon={TrendingUp}
          label="Service Sales"
          value={fmt(m?.sales)}
          sub={`from ${fmt(m?.leads?.total)} service leads`}
          accent="emerald"
        />
        <PrimaryKpi
          icon={Target}
          label="Service Leads"
          value={fmt(m?.leads?.total)}
          sub={`${fmt(m?.leads?.active)} active in pipeline`}
          accent="blue"
        />
        <PrimaryKpi
          icon={Users}
          label="Conversion"
          value={`${m?.conversion_pct || 0}%`}
          sub={adSpendSub}
          accent="violet"
        />
      </div>

      {/* Row 1.2 — Payment links sent in this range and where they stand now */}
      <ServicePaymentLinks period={period} />

      {/* Row 1.5 — "Received" breakdown (finance only): new advance vs old collections + source split */}
      {!revRedacted && m?.revenue && (
        <ReceivedBreakdown revenue={m.revenue} asOf={m.as_of} onSourceClick={setDrillChannel} />
      )}
      {drillChannel && (
        <ReceivedDrilldown period={period} channel={drillChannel} onClose={() => setDrillChannel(null)} />
      )}

      {/* Source Performance matrix — marketing cohort (defaults to Cash received, reconciles with Revenue by Source) */}
      {!revRedacted && (
        <SourcePerformance
          sperf={sperf}
          gads={g}
          strend={strend}
          srcCity={srcCity}
          onCityChange={setSrcCity}
          srcBasis={srcBasis}
          onBasisChange={setSrcBasis}
          srcLoading={srcLoading}
        />
      )}

      {/* City Deep-Dive analytics engine (Google spend vs CRM reality, per city) */}
      {!revRedacted && <CityAnalysis period={period} />}

      {/* Service Sales by Executive (event-based; sums to hero Sales / Revenue) */}
      <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-by-executive">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-700">Service Sales by Executive</h3>
          <span className="text-xs text-slate-400 ml-auto">Top 50</span>
        </div>
        {!r?.rows?.length ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No executive activity in this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="svc-rep-table">
              <thead>
                <tr className="text-xs font-medium text-slate-500 uppercase tracking-wide border-b border-slate-100">
                  <th className="text-left py-2 px-2">Executive</th>
                  <th className="text-right py-2 px-2">Leads</th>
                  <th className="text-right py-2 px-2">Sales</th>
                  <th className="text-right py-2 px-2">Conv %</th>
                  <th className="text-right py-2 px-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((row) => (
                  <tr key={row.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-2 px-2 text-slate-800 font-medium">{row.name}</td>
                    <td className="py-2 px-2 text-right text-slate-700">{fmt(row.total_leads)}</td>
                    <td className="py-2 px-2 text-right text-emerald-700 font-medium">{fmt(row.sales ?? row.booked)}</td>
                    <td className="py-2 px-2 text-right text-slate-600">{row.conv_pct}%</td>
                    <td className="py-2 px-2 text-right font-semibold text-slate-900">
                      {r.revenue_redacted ? "—" : fmtK(row.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Row 2 — 3 funnel pulse cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="svc-funnel-row">
        <FunnelCard
          label="Draft to Contact"
          count={f?.draft_to_contact?.count || 0}
          pool={f?.in_pipeline || 0}
          pct={f?.draft_to_contact?.pct || 0}
          accent="amber"
        />
        <FunnelCard
          label="Quoted to Booked"
          count={f?.quoted_to_booked?.count || 0}
          pool={f?.in_pipeline || 0}
          pct={f?.quoted_to_booked?.pct || 0}
          accent="violet"
        />
        <FunnelCard
          label="Same-Day Bookings"
          count={f?.same_day_bookings?.count || 0}
          pool={f?.in_pipeline || 0}
          pct={f?.same_day_bookings?.pct || 0}
          accent="emerald"
        />
      </div>

      {/* Row 3 — Expenditure breakdown */}
      <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-expenditure">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-4 h-4 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-700">Service Expenditure</h3>
          {e?.forbidden && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-medium ml-auto">
              Hidden for your role
            </span>
          )}
        </div>
        {e?.forbidden ? (
          <p className="text-sm text-slate-400 py-4">
            Ask a finance admin to grant you the FINANCE_HEAD or COUNTRY_HEAD role to see expenditure.
          </p>
        ) : (
          <>
            <div className="space-y-1">
              <ChannelRow
                name="Google Ads"
                amount={e?.channels?.google_ads?.amount}
                status={e?.channels?.google_ads?.status}
              />
              <ChannelRow
                name="Meta Ads"
                amount={e?.channels?.meta_ads?.amount}
                status={e?.channels?.meta_ads?.status}
              />
              <ChannelRow
                name="Manual / Other"
                amount={e?.channels?.manual?.amount}
                status={e?.channels?.manual?.status}
              />
            </div>
            <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100">
              <span className="text-xs font-medium text-slate-500 uppercase">Total</span>
              <span className="text-lg font-bold text-slate-900">{fmtK(e?.total)}</span>
            </div>
            {e?.channels?.google_ads?.status === "pending_approval" && (
              <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-800">
                <strong>Google Ads pending approval</strong> — Basic Access application
                under review (Case 3-9062000041090). Spend will auto-populate
                once Google approves (2–5 business days).
              </div>
            )}
          </>
        )}
      </div>

      {/* Row 3.5 — Live Google Ads performance (straight from the Google Ads API) */}
      {!g.forbidden && (g.configured || g.error) && (
        <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-google-ads-live">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-slate-700">Google Ads — Live Performance</h3>
            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">LIVE API</span>
            {g.date_range && (
              <span className="text-xs text-slate-400 ml-auto">{g.date_range.replace(/_/g, " ").toLowerCase()}</span>
            )}
          </div>
          {g.error ? (
            <p className="text-sm text-slate-400 py-3">Couldn’t reach Google Ads right now. Retry shortly.</p>
          ) : !g.campaigns?.length ? (
            <p className="text-sm text-slate-400 py-3">No campaign activity in this period.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="svc-google-ads-totals">
                <GadsStat label="Spend" value={fmtK(g.totals?.cost)} />
                <GadsStat label="Clicks" value={fmt(g.totals?.clicks)} />
                <GadsStat label="CTR" value={`${g.totals?.ctr ?? 0}%`} />
                <GadsStat label="Conversions" value={fmt(g.totals?.conversions)} />
                <GadsStat label="Cost / Conv." value={fmtK(g.totals?.cpl)} accent="amber" />
              </div>
              {/* ROAS — real CRM revenue from gclid leads ÷ ad spend (this period) */}
              {g.roas && (
                <div
                  className="grid grid-cols-3 gap-3 mb-4 rounded-lg bg-emerald-50/50 border border-emerald-100 p-2"
                  data-testid="svc-google-ads-roas"
                >
                  <GadsStat label="Ad Spend" value={fmtK(g.roas.spend)} />
                  <GadsStat label="Revenue · Ads leads" value={fmtK(g.roas.revenue)} accent="emerald" />
                  <GadsStat
                    label={`ROAS · ${fmt(g.roas.leads)} leads`}
                    value={g.roas.roas != null ? `${g.roas.roas}×` : "—"}
                    accent={g.roas.roas != null && g.roas.roas >= 1 ? "emerald" : "amber"}
                  />
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="svc-google-ads-table">
                  <thead>
                    <tr className="text-xs font-medium text-slate-500 uppercase tracking-wide border-b border-slate-100">
                      <th className="text-left py-2 px-2">Campaign</th>
                      <th className="text-right py-2 px-2">Impr.</th>
                      <th className="text-right py-2 px-2">Clicks</th>
                      <th className="text-right py-2 px-2">CTR</th>
                      <th className="text-right py-2 px-2">Spend</th>
                      <th className="text-right py-2 px-2">Conv.</th>
                      <th className="text-right py-2 px-2">Cost/Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.campaigns.map((c) => (
                      <tr key={c.campaign_id}
                          className="border-b border-slate-50 hover:bg-slate-50"
                          data-testid={`gads-campaign-${c.campaign_id}`}>
                        <td className="py-2 px-2">
                          <div className="font-medium text-slate-800">{c.campaign_name}</div>
                          <div className="text-[11px] text-slate-400">{c.status}</div>
                        </td>
                        <td className="text-right py-2 px-2 text-slate-600">{fmt(c.impressions)}</td>
                        <td className="text-right py-2 px-2 text-slate-600">{fmt(c.clicks)}</td>
                        <td className="text-right py-2 px-2 text-slate-600">{c.ctr}%</td>
                        <td className="text-right py-2 px-2 font-semibold text-slate-900">{fmtK(c.cost)}</td>
                        <td className="text-right py-2 px-2 text-slate-600">{c.conversions}</td>
                        <td className="text-right py-2 px-2 text-amber-700 font-medium">{fmtK(c.cpl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">
                Google’s in-tag value is ≈ ₹1/lead; the <strong>ROAS</strong> above uses real CRM revenue from
                click-attributed (gclid) leads booked this period, now that offline conversions (Data Manager API) are live.
              </p>
            </>
          )}
        </div>
      )}

      {/* Source Performance + City Deep-Dive + Sales by Executive moved up — see below Revenue by Source */}

      {/* Row 3.6 — Google Ads Offline Conversions (Data Manager API push loop) */}
      {!o.forbidden && o.configured != null && (
        <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-offline-conversions">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-700">Google Ads — Offline Conversions</h3>
            {!o.enabled ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">OFF</span>
            ) : o.validate_only ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">VALIDATING</span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">LIVE</span>
            )}
            <button
              onClick={runSweep}
              disabled={sweeping || !o.configured}
              className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded-md border border-emerald-200 disabled:opacity-50"
              data-testid="svc-oc-run-sweep"
            >
              {sweeping ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Run sweep now
            </button>
          </div>

          {!o.configured ? (
            <p className="text-sm text-slate-400 py-3">
              Not configured yet — awaiting the Data Manager API refresh token.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3" data-testid="svc-oc-totals">
                <GadsStat label="Booked pushed" value={fmt(o.totals?.booked_sent)} />
                <GadsStat label="Paid pushed"   value={fmt(o.totals?.paid_sent)} accent="amber" />
                <GadsStat label="Errors"        value={fmt(o.totals?.errors)} />
              </div>
              {/* Live "sent to Google today" ticker */}
              {o.today && (o.today.paid_sent > 0 || o.today.booked_sent > 0) ? (
                <div className="mb-3 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-100 text-[12px] text-emerald-800 flex items-center gap-1.5"
                     data-testid="svc-oc-today">
                  <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    Sent to Google today: <strong>{fmt(o.today.paid_sent)} Paid</strong>
                    {" · "}<strong>{fmtK(o.today.paid_value)}</strong> value
                    {o.today.booked_sent > 0 && <> · {fmt(o.today.booked_sent)} Booked</>}
                  </span>
                </div>
              ) : o.today ? (
                <div className="mb-3 text-[11px] text-slate-400" data-testid="svc-oc-today">
                  No conversions sent to Google yet today.
                </div>
              ) : null}
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span data-testid="svc-oc-last-sync">
                  {o.last_sweep?.swept_at
                    ? `Last sync: ${new Date(o.last_sweep.swept_at).toLocaleString("en-IN")}`
                    : "No sweep run yet"}
                </span>
                <span>Acct {o.operating_account} · Booked #{o.conversion_actions?.booked} · Paid #{o.conversion_actions?.paid}</span>
              </div>
              {o.validate_only && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-800">
                  <strong>Validation mode</strong> — Google is checking each event but not
                  counting conversions yet. Flip <code>GOOGLE_DM_VALIDATE_ONLY=false</code> to go live.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Row 3.7 — WhatsApp "chat started" conversions (founder-only).
          A tap on the WhatsApp button is NOT a lead; a real inbound chat from a
          Google click is. These rows are what SocialOrange upload to Google Ads. */}
      {!w.forbidden && w.configured != null && (
        <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="svc-wa-chat-conversions">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-semibold text-slate-700">WhatsApp — Chat-Started Conversions</h3>
            {!w.enabled ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-medium" data-testid="svc-wac-badge">OFF</span>
            ) : !w.configured ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-600 font-medium" data-testid="svc-wac-badge">QUEUEING</span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium" data-testid="svc-wac-badge">LIVE</span>
            )}
            <span className="text-[10px] text-slate-400">founders only</span>
            <button
              onClick={runWacSweep}
              disabled={wacSweeping}
              className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded-md border border-green-200 disabled:opacity-50"
              data-testid="svc-wac-run-sweep"
            >
              {wacSweeping ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send queue now
            </button>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-3" data-testid="svc-wac-totals">
            <GadsStat label="Chats counted" value={fmt(w.totals?.sent)} accent="emerald" />
            <GadsStat label="Waiting to send" value={fmt(w.totals?.pending)} accent="amber" />
            <GadsStat label="Rejected" value={fmt((w.totals?.rejected || 0) + (w.totals?.dead || 0))} />
            <GadsStat label={`Deduped (${w.dedupe_days || 30}d)`} value={fmt(w.totals?.skipped_dedupe)} />
          </div>

          {!w.configured ? (
            <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-800" data-testid="svc-wac-not-configured">
              <strong>Waiting on SocialOrange</strong> — every qualifying chat is being saved
              and nothing is lost. Set <code>SOCIALORANGE_CHAT_CONVERSION_URL</code> (and the
              shared secret) and they all upload on the next sweep.
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 flex items-center justify-between" data-testid="svc-wac-meta">
              <span>Counted today: <strong className="text-slate-600">{fmt(w.sent_today)}</strong></span>
              <span>
                {w.last_sweep?.swept_at
                  ? `Last sweep: ${new Date(w.last_sweep.swept_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`
                  : "No sweep run yet"}
                {" · auth "}{w.auth_mode}
              </span>
            </div>
          )}

          {w.stuck_since && (
            <div className="mt-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12px] text-red-800" data-testid="svc-wac-stuck">
              <strong>Queue stuck</strong> since {new Date(w.stuck_since).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} —
              real chats from paid clicks are not reaching Google Ads.
            </div>
          )}

          {(w.recent || []).length > 0 && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-[11px]" data-testid="svc-wac-recent">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="text-left py-1.5 pr-2 font-medium">Chat</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Click id</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Type</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Resolved from</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(w.recent || []).map((row) => (
                    <tr key={row.id} className="border-b border-slate-50">
                      <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap">
                        {row.chat_started_at
                          ? new Date(row.chat_started_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                          : "—"}
                      </td>
                      <td className="py-1.5 pr-2 font-mono text-slate-500">{row.click_id}</td>
                      <td className="py-1.5 pr-2 text-slate-600">{row.click_id_type}</td>
                      <td className="py-1.5 pr-2 text-slate-400">
                        {row.click_id_type_source}
                        {row.click_id_type_source === "default_guess" && (
                          <span className="ml-1 text-amber-600" title="Type not proven — Google may reject it">!</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2">
                        <span className={
                          row.status === "SENT" ? "text-emerald-600 font-medium"
                            : row.status === "REJECTED" || row.status === "DEAD" ? "text-red-600 font-medium"
                            : row.status === "SKIPPED_PHONE_DEDUPE" ? "text-slate-400"
                            : "text-amber-600"
                        }>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
